/**
 * Chat Thread Realtime State
 *
 * Pure helpers for applying realtime message changes to a visible chat
 * thread without waiting for a full thread reload.
 *
 * Primary responsibilities:
 * - Convert message rows into `ChatMessage` view models
 * - Upsert persisted messages over local thread state
 * - Remove deleted messages and merge hydrated attachments
 *
 * Dependencies:
 * - lib/messages
 */

import { parseDBMessageCodeBlocksAndContent } from '@/lib/messages'
import { sortChatMessagesByThreadOrder } from '@/lib/chat/message-order'
import { reconcileMessage, reconcileMessages } from '@/lib/chat/message-reconciler'
import { Tables } from '@/supabase/types'
import type { ChatMessage, MessageImage } from '@/types'

type RealtimeFileItemReference = {
  id: string
}

export type RealtimeChatMessageRow = Tables<'messages'> & {
  file_items?: RealtimeFileItemReference[]
}

export interface RealtimeDeletedChatMessage {
  chatId: string | null
  messageId: string | null
  sequenceNumber: number | null
}

// --------------

function mergeChatMessage(previousMessage: ChatMessage, nextMessage: ChatMessage): ChatMessage {
  return reconcileMessage(previousMessage, nextMessage, 'realtime')
}

function matchesMessageIdentity(
  chatMessage: ChatMessage,
  nextMessage: ChatMessage
) {
  if (chatMessage.message.id === nextMessage.message.id) {
    return true
  }

  return (
    chatMessage.message.chat_id === nextMessage.message.chat_id &&
    chatMessage.message.sequence_number === nextMessage.message.sequence_number
  )
}

function buildRealtimeImageKey(image: MessageImage) {
  return `${image.messageId}:${image.path || image.url || ''}`
}

// --------------

export function buildRealtimeChatMessage(
  message: RealtimeChatMessageRow
): ChatMessage {
  return parseDBMessageCodeBlocksAndContent({
    ...message,
    metadata: message.metadata ?? null,
    type: message.type ?? null,
    file_items: message.file_items ?? []
  })
}

export function upsertParsedRealtimeChatMessage(
  currentMessages: ChatMessage[],
  nextMessage: ChatMessage
) {
  // Route through the reconciler so a lagging realtime echo cannot downgrade a
  // terminal message, and — critically — so an unchanged echo returns the SAME
  // array reference (no per-200ms render cascade / React #185).
  return reconcileMessages(currentMessages, [nextMessage], 'realtime')
}

export function upsertRealtimeChatMessage(
  currentMessages: ChatMessage[],
  message: RealtimeChatMessageRow
) {
  return upsertParsedRealtimeChatMessage(
    currentMessages,
    buildRealtimeChatMessage(message)
  )
}

export function removeRealtimeChatMessage(
  currentMessages: ChatMessage[],
  deletedMessage: RealtimeDeletedChatMessage
) {
  return currentMessages.filter((chatMessage) => {
    if (deletedMessage.messageId && chatMessage.message.id === deletedMessage.messageId) {
      return false
    }

    if (
      deletedMessage.chatId &&
      deletedMessage.sequenceNumber !== null &&
      chatMessage.message.chat_id === deletedMessage.chatId &&
      chatMessage.message.sequence_number === deletedMessage.sequenceNumber
    ) {
      return false
    }

    return true
  })
}

export function mergeRealtimeFileItems(
  currentFileItems: Tables<'file_items'>[],
  nextFileItems: Tables<'file_items'>[]
) {
  const mergedFileItems = [...currentFileItems]
  const seenFileItemIds = new Set(currentFileItems.map((fileItem) => fileItem.id))

  nextFileItems.forEach((fileItem) => {
    if (seenFileItemIds.has(fileItem.id)) {
      return
    }

    seenFileItemIds.add(fileItem.id)
    mergedFileItems.push(fileItem)
  })

  return mergedFileItems
}

export function mergeRealtimeMessageImages(
  currentImages: MessageImage[],
  nextImages: MessageImage[]
) {
  const mergedImages = [...currentImages]
  const seenImageKeys = new Set(currentImages.map(buildRealtimeImageKey))

  nextImages.forEach((image) => {
    const imageKey = buildRealtimeImageKey(image)

    if (seenImageKeys.has(imageKey)) {
      return
    }

    seenImageKeys.add(imageKey)
    mergedImages.push(image)
  })

  return mergedImages
}
